import axios from 'axios';
import cookies from 'js-cookie';
import ApiError, {ApiIsLoginError, NotLoginCode} from './cms-errors'
import {requestWithRetry} from './cms-http-retry'

const axiosInstance = axios.create();

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong')
);

export default axiosInstance;

// ----------------------------------------------------------------------
// 再試行用の素のインスタンス
// ----------------------------------------------------------------------
// 上の axiosInstance のインターセプタは失敗時に error.response.data だけを
// 残すため、HTTP ステータスが消えて「401 は再試行しない / 5xx は再試行する」
// の判定ができない。再試行が要る httpGet / httpPost はインターセプタなしの
// こちらを使い、最後に同じ変換をこの層でかける (throw する値は従来と同じ)。
// auth-provider.js / page_meta.js が使う default export は据え置き。
const retryAxios = axios.create();

// 送信 1 回ぶん。requestWithRetry から呼ばれる。
const sendRaw = (config) => retryAxios.request(config);

// 従来の response インターセプタと同じ変換。
const toLegacyError = (error) => (error.response && error.response.data) || 'Something went wrong';

async function sendWithRetry(config) {
  try {
    return await requestWithRetry(sendRaw, config, {
      onRetry: ({url, attempt, maxRetries, delay}) => {
        console.warn(`[cms-axios] retry ${attempt}/${maxRetries} in ${delay}ms: ${url}`);
      },
    });
  } catch (error) {
    throw toLegacyError(error);
  }
}

// ----------------------------------------------------------------------

export async function httpGet(url, params = {}, headers = {}, prefix = "/api", isBlob = false) {
  const api_key = cookies.get('api_key')
  if (!api_key) {
    const apiError = new ApiError("not login")
    apiError.ErrorCode = NotLoginCode
    throw apiError
  }

  // ⚠ httpGet は **再試行しない**。cmsIdempotentRead を付けないため。
  //   GET は冪等だが、504 を返している後端に投げ足すと症状が悪化する
  //   (/user と /config-api/site-admin-config で実際に 504 を観測)。
  //   間欠 500 で画面が空になるのを防ぎたい一覧取得は httpGetRead を使う。
  const config = {
    method: 'get',
    url: `${prefix}${url}`,
    params,
    headers,
  };

  if (isBlob) {
    config.responseType = 'blob';
  }

  const response = await sendWithRetry(config);

  if (isBlob) {
    return response.data; // For blob responses, return the raw data
  }

  const {status, error} = response.data;

  if (status !== 'OK') {
    if (ApiIsLoginError(error)) {
      const apiError = new ApiError("not login")
      apiError.ErrorCode = NotLoginCode
      throw apiError
    }
    const errorMessage = typeof error === 'object' && error?.error ? error.error : error;
    throw new Error(errorMessage);
  }

  return response.data
}

export async function httpPost(url, data = {}, headers = {}, prefix = "/api") {
  const api_key = cookies.get('api_key')
  if (!api_key) {
    const apiError = new ApiError("not login")
    apiError.ErrorCode = NotLoginCode
    throw apiError
  }

  // ⚠ httpPost は **再試行しない**。cmsIdempotentRead を付けないため、
  //   保存系がここを通っても決して再送されない。
  //   副作用のない一覧取得は httpPostRead を使う。
  const response = await sendWithRetry({
    method: 'post',
    url: `${prefix}${url}`,
    data,
    headers,
  });

  const {status, error} = response.data;

  if (status !== 'OK') {
    if (ApiIsLoginError(error)) {
      const apiError = new ApiError("not login")
      apiError.ErrorCode = NotLoginCode
      throw apiError
    }
    const errorMessage = typeof error === 'object' && error?.error ? error.error : error;
    throw new Error(errorMessage);
  }

  if ('cms-site-id' in headers) {
    if (url === '/post/create'
      || url === '/post/update'
      || url === '/post/delete'
      || url === '/post/change_rank'
      || url === 'post/change_top_last_rank'
      || url === '/post/type/create'
      || url === '/post/type/update'
      || url === '/post/type/delete'
      || url === '/category/create'
      || url === '/category/update'
      || url === '/category/delete'
      || url === '/category/rank/update'
      || url === '/page/create'
      || url === '/page/update'
      || url === '/page/delete'
      || url === '/page/post/create'
      || url === '/page/post/update'
    ) {
      pageCacheMasterVersionUpdate(headers['cms-site-id']).then({});
    }
  }

  return response.data
}

// ----------------------------------------------------------------------
// status が OK でなくても応答をそのまま返す POST（保存系）
// ----------------------------------------------------------------------
// httpPost は status !== 'OK' のとき error をメッセージだけの Error にして投げる。
// エラーの**コード**で分岐したい保存（管理画面設定の U000132 = 他の人が保存済み）は
// それだと判定できないので、こちらで応答を受け取り、呼び出し側で扱う。
//
// ⚠ 再試行しない・未ログインは httpPost と同じく ApiError で投げる。
//   通信エラーも同じ変換（toLegacyError）で投げる。
//   httpPost の保存後の後処理（ページキャッシュの版上げ）は持たない —— 使うのは
//   /config-api の設定保存だけで、あちらの対象 URL には入っていない。
export async function httpPostResult(url, data = {}, headers = {}, prefix = "/api") {
  const api_key = cookies.get('api_key')
  if (!api_key) {
    const apiError = new ApiError("not login")
    apiError.ErrorCode = NotLoginCode
    throw apiError
  }

  let body;
  try {
    const response = await sendWithRetry({
      method: 'post',
      url: `${prefix}${url}`,
      data,
      headers,
    });
    body = response.data;
  } catch (e) {
    // ⚠ サーバは USER_ERROR 等を 2xx 以外の HTTP ステータスで返すことがあり、
    //   そのとき axios は reject し、sendWithRetry は応答本文をそのまま投げる。
    //   本文（status を持つ JSON）なら投げずに返す —— ここはそれを読むための口。
    //   2026-09-27 ローカル実機: U000132 が素のオブジェクトのまま画面まで届き、
    //   確認ダイアログではなく赤いエラー表示になった。
    if (!e || typeof e !== 'object' || typeof e.status !== 'string') throw e;
    body = e;
  }

  const {status, error} = body || {};
  if (status !== 'OK' && ApiIsLoginError(error)) {
    const apiError = new ApiError("not login")
    apiError.ErrorCode = NotLoginCode
    throw apiError
  }
  return body
}

// ----------------------------------------------------------------------
// 副作用のない GET（一覧取得）専用
// ----------------------------------------------------------------------
// 間欠 500 で一覧が空になるのを防ぎたいものだけをこちらに寄せる。
// 現在の利用箇所: /form/list_by_page_pkg_code
//   （デザインエディタが起動時に並列で投げる 5 本のうちの GET 1 本）
export async function httpGetRead(url, params = {}, headers = {}, prefix = "/api") {
  const api_key = cookies.get('api_key')
  if (!api_key) {
    const apiError = new ApiError("not login")
    apiError.ErrorCode = NotLoginCode
    throw apiError
  }

  const response = await sendWithRetry({
    method: 'get',
    url: `${prefix}${url}`,
    params,
    headers,
    // 冪等な読み取りであることの申告。これが無いと再試行されない。
    cmsIdempotentRead: true,
  });

  const {status, error} = response.data;

  if (status !== 'OK') {
    if (ApiIsLoginError(error)) {
      const apiError = new ApiError("not login")
      apiError.ErrorCode = NotLoginCode
      throw apiError
    }
    const errorMessage = typeof error === 'object' && error?.error ? error.error : error;
    throw new Error(errorMessage);
  }

  return response.data
}

// ----------------------------------------------------------------------
// 副作用のない POST（一覧取得・検索クエリ）専用
// ----------------------------------------------------------------------
// 後端の一覧 API は POST だが、pkg_code / limit / offset を受けて
// リストを返すだけで後端の状態を変えない。こちらだけ再試行の対象にする。
//
// ⚠ **ここに足すのは「同じリクエストを 3 回投げても後端の状態が変わらない」
//   と確認できたものだけ。** 判断に迷うものは httpPost を使う
//   （再試行されないので安全側に倒れる）。
//   現在の利用箇所: /page/list, /template/list,
//   /template/layout/list, /template/block/list
export async function httpPostRead(url, data = {}, headers = {}, prefix = "/api") {
  const api_key = cookies.get('api_key')
  if (!api_key) {
    const apiError = new ApiError("not login")
    apiError.ErrorCode = NotLoginCode
    throw apiError
  }

  const response = await sendWithRetry({
    method: 'post',
    url: `${prefix}${url}`,
    data,
    headers,
    // 冪等な読み取りであることの申告。これが無いと再試行されない。
    cmsIdempotentRead: true,
  });

  const {status, error} = response.data;

  if (status !== 'OK') {
    if (ApiIsLoginError(error)) {
      const apiError = new ApiError("not login")
      apiError.ErrorCode = NotLoginCode
      throw apiError
    }
    const errorMessage = typeof error === 'object' && error?.error ? error.error : error;
    throw new Error(errorMessage);
  }

  return response.data
}

export function pageCacheMasterVersionUpdate(site_id) {
  return httpPost("/shopify-page-cache-master-version-update", {}, {
    "cms-site-id": site_id,
  }, "");
}
